import {
  Typography,
  Container,
  Grid2,
  Box,
  Stack,
  useTheme,
  CircularProgress,
  BoxProps,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import PageHeader from '../../../core/components/layout/PageHeader';
import { useSelector } from 'react-redux';
import { RootState } from '../../../core/store';
import { GradientContainer, InfoCard } from './StyledComponent';
import BlockContainer from '../../../core/components/layout/BlockContainer';
import { extranalColor } from '../../../core/theme/mindright';
import { Button } from '../../../core/components';
import { Add } from '@mui/icons-material';
import { useGetDashboardStatisticsQuery } from '../api/dashboard.api';
import ApiErrorLabel from '../../../core/components/ui/ApiErrorLabel';
import { useNavigate } from 'react-router-dom';

const SectionHeader = ({ title, icon, bg }: { title: string; icon: string; bg?: string }) => {
  const theme = useTheme();
  return (
    <Stack
      direction={'row'}
      alignItems={'center'}
      justifyContent={'space-between'}
      borderBottom={1}
      borderColor={theme.palette.divider}
      style={{
        margin: '0px -20px',
        padding: '10px 25px',
        paddingBottom: '20px',
      }}
    >
      <Typography
        variant='h6'
        sx={{
          fontSize: `${theme.typography.fontSize * 1.2}px`,
        }}
        fontWeight={'500'}
      >
        {title}
      </Typography>
      <Box
        width={35}
        height={35}
        borderRadius={2}
        bgcolor={bg}
        display={'flex'}
        justifyContent={'center'}
        alignItems={'center'}
      >
        {icon}
      </Box>
    </Stack>
  );
};
const ContentBox = ({ children, sx }: { children: React.ReactNode; sx: BoxProps }) => {
  const theme = useTheme();
  return (
    <Box
      bgcolor={theme.palette.background.default}
      padding={3}
      borderRadius={3}
      height={'100%'}
      {...sx}
    >
      {children}
    </Box>
  );
};

const StatisticBox = ({
  count,
  countFontSize = 40,
  countColor,
  label,
  labelFontSize = 15,
  labelColor,
  isLoading,
}: {
  count: string | number;
  countFontSize?: number | string;
  countColor?: string;
  label: string;
  labelFontSize?: number | string;
  labelColor?: string;
  isLoading: boolean;
}) => {
  return (
    <ContentBox sx={{ alignItems: 'center', display: 'flex', flexDirection: 'column' }}>
      {isLoading ? (
        <CircularProgress size='30px' style={{ marginBottom: '18px' }} />
      ) : (
        <Typography
          variant='h6'
          sx={{
            fontSize: countFontSize,
            fontWeight: '600',
            color: countColor,
            textAlign: 'center',
          }}
        >
          {count}
        </Typography>
      )}
      <Typography
        variant='inherit'
        sx={{
          color: labelColor,
          textAlign: 'center',
          fontSize: labelFontSize,
        }}
      >
        {label}
      </Typography>
    </ContentBox>
  );
};
function Dashboard() {
  const personalDetails = useSelector((state: RootState) => state.personalDetails);
  const theme = useTheme();
  const navigate = useNavigate();
  const { data, error, isError, isLoading, refetch } = useGetDashboardStatisticsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [isErrorFetching, setErrorFetching] = useState(false);
  const messageSent = data?.data.data.totalMessageSent ?? 0;
  const messageReceive = data?.data.data.totalMessageReceive ?? 0;
  const availableMoments = data?.data.data.availableMoments ?? 0;

  useEffect(() => {
    setErrorFetching(isError);
  }, [isError]);

  useEffect(() => {
    (async () => {
      await refetch();
    })();
  }, []);

  return (
    <Container maxWidth='xl'>
      <PageHeader
        title={`Welcome back ${personalDetails?.firstName}`}
        description='You mental wellness journey continues today.'
      />
      <GradientContainer mt={4}>
        <Typography variant='h5' mb={3}>
          📢 Announcements
        </Typography>
        <Grid2 container size={12} spacing={3}>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <InfoCard
              title='Join Our New Member Orientation'
              description='Every Wednesday at 11:30 AM ET'
              linkText='Reserve Your Spot'
              linkUrl='https://bit.ly/MindRightOrientation'
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <InfoCard
              title='Bonus Content'
              description='Check out more from the founder'
              linkText='Watch Now'
              linkUrl='https://www.youtube.com/@heyashleyedwards/streams'
            />
          </Grid2>
        </Grid2>
      </GradientContainer>

      <GradientContainer mt={4} mb={4}>
        <Box>
          <Typography fontStyle='italic' variant='h6' mb={3}>
            "Self-Care is not self-indulgence, it is self-preservation"
          </Typography>
          <Typography variant='body1' mb={3}>
            - Audre Lorde
          </Typography>
        </Box>
      </GradientContainer>
      <Grid2 container spacing={4} alignItems={'stretch'}>
        <Grid2 size={{ lg: 8, xs: 12 }}>
          <BlockContainer sx={{ height: '100%' }}>
            <SectionHeader title='Message Activity' icon='💬' bg={theme.palette.primary[400]} />
            <Box margin={4} />
            {/* <StatisticBox
                            count={28}
                            label={"Messages This Week"}
                            countColor={theme.palette.primary[400]}
                            labelColor={theme.palette.grey[500]}
                        />

                        <Box margin={4} />
                        <ContentBox>
                            <Typography variant='h6' sx={{
                                color: theme.palette.grey[800],
                                fontSize: 16,
                                fontWeight: '500'
                            }}>Weekly Communication Rhythm</Typography>
                            <Box>
                                <BarChartComponent/>
                            </Box>
                        </ContentBox>
                        <Box margin={4} /> */}
            <Grid2 container spacing={3} alignItems={'stretch'}>
              <Grid2 size={{ lg: 6, xs: 6 }}>
                <StatisticBox
                  count={messageSent}
                  countFontSize={30}
                  label={'Messages Sent'}
                  countColor={theme.palette.primary[400]}
                  labelColor={theme.palette.grey[500]}
                  isLoading={isLoading}
                />
              </Grid2>
              <Grid2 size={{ lg: 6, xs: 6 }}>
                <StatisticBox
                  count={messageReceive}
                  countFontSize={30}
                  label={'Messages Received'}
                  countColor={theme.palette.warning[300]}
                  labelColor={theme.palette.grey[500]}
                  isLoading={isLoading}
                />
              </Grid2>
              <Grid2 size={12}>
                <ApiErrorLabel
                  message={error as string}
                  show={isErrorFetching}
                  variant='alert'
                  testId='api-error'
                  sx={{ mb: 2 }}
                  onDismiss={() => setErrorFetching(false)}
                  dismissible={true}
                />
              </Grid2>
            </Grid2>
          </BlockContainer>
        </Grid2>
        <Grid2 size={{ lg: 4, xs: 12 }}>
          <BlockContainer sx={{ height: '100%' }}>
            <SectionHeader title='MindRight Moments Available' icon='💰' bg={extranalColor.aqua} />
            <Stack direction={'column'} alignItems={'center'} justifyItems={'center'}>
              <Box
                padding={4}
                display={'flex'}
                flexDirection={'column'}
                alignItems={'center'}
                justifyItems={'center'}
              >
                {isLoading ? (
                  <CircularProgress size='40px' style={{ margin: '36px 0px' }} />
                ) : (
                  <Typography
                    variant='h6'
                    sx={{
                      fontSize: 70,
                      fontWeight: '500',
                      color: extranalColor.aqua,
                      textAlign: 'center',
                    }}
                  >
                    {availableMoments}
                  </Typography>
                )}
                <Typography
                  variant='inherit'
                  sx={{
                    color: theme.palette.grey[500],
                    textAlign: 'center',
                    fontSize: 16,
                    fontWeight: '400',
                  }}
                >
                  Available Moments
                </Typography>
              </Box>

              <Button
                startIcon={<Add />}
                onClick={() => navigate('/subscriptions?addOn=true')}
                variant='contained'
                style={{
                  background: theme.palette.grey[200],
                  color: theme.palette.primary[400],
                  borderRadius: 100,
                  padding: '8px 20px',
                }}
              >
                Add More Moments
              </Button>
            </Stack>
          </BlockContainer>
        </Grid2>
      </Grid2>
    </Container>
  );
}

export default Dashboard;
